/**
 * ERP Variant Mapper - Client-Side Only Version
 * Robust client-side search with comprehensive fallback strategies
 */

class ERPVariantMapperClientOnly {
  constructor(config = {}) {
    this.cache = new Map();
    this.cacheExpiry = 12 * 60 * 60 * 1000;
    this.cacheVersion = '2.0.0';
    this.storeVersion = window.Shopify?.shop || 'default';
    
    // Track which pages we've already fetched
    this.fetchedPages = new Set();
    this.allPagesFetched = false;
    
    // Configuration
    this.config = {
      debug: config.debug || false,
      maxSearchPages: config.maxSearchPages || 40,
    };
    
    this.loadCacheFromStorage();
    this.setupInvalidationListeners();
  }

  debug(...args) {
    if (this.config.debug) {
      console.log('[ERP Mapper]', ...args);
    }
  }

  /**
   * Get variant by ERP code - Client-side only
   */
  async getVariantByErpCode(erpCode, options = {}) {
    const {
      forceRefresh = false,
      maxAge = this.cacheExpiry,
    } = options;

    // Check cache first
    if (!forceRefresh && this.cache.has(erpCode)) {
      const cached = this.cache.get(erpCode);
      if (!cached._cachedAt || Date.now() - cached._cachedAt <= maxAge) {
        this.debug(`✅ Cache hit for ${erpCode}`);
        return cached;
      }
      this.debug(`⚠️ Cache entry stale for ${erpCode}`);
      this.cache.delete(erpCode);
    }

    this.debug(`🔍 Searching for ERP code: ${erpCode}`);

    // Client-side search strategies
    const variant = await this.searchClientSide(erpCode);
    
    if (variant) {
      this.debug(`✅ Found: ${variant.product_title} - ${variant.title}`);
      this.cacheVariant(erpCode, variant);
      return variant;
    }

    console.warn(`[ERP Mapper] ❌ Could not find variant for: ${erpCode}`);
    return null;
  }

  /**
   * Client-side search - directly use products.json since search API doesn't index SKUs
   */
  async searchClientSide(erpCode) {
    try {
      return await this.searchAllProducts(erpCode);
    } catch (error) {
      console.error('[ERP Mapper] Search failed:', error);
      return null;
    }
  }

  /**
   * Search through all products (paginated)
   * Cache ENTIRE pages, then check if we found what we need
   */
  async searchAllProducts(erpCode) {
    try {
      // First check if already cached
      if (this.cache.has(erpCode)) {
        this.debug(`✅ Found in cache: ${erpCode}`);
        return this.cache.get(erpCode);
      }

      // If we've already fetched all pages, it doesn't exist
      if (this.allPagesFetched) {
        this.debug(`All pages already fetched, SKU ${erpCode} doesn't exist`);
        return null;
      }

      // Start from where we left off
      let page = this.fetchedPages.size + 1;
      const maxPages = this.config.maxSearchPages;
      let foundVariant = null;
      let totalCachedThisRun = 0;

      this.debug(`Continuing from page ${page}, cache has ${this.cache.size} SKUs`);

      while (page <= maxPages) {
        // Build URL with manual page parameter
        const url = `/products.json?limit=250&page=${page}`;
        
        this.debug(`Fetching page ${page}...`);
        
        const response = await fetch(url);
        if (!response.ok) {
          this.debug(`API returned ${response.status}`);
          break;
        }

        const data = await response.json();
        const products = data.products || [];
        
        if (products.length === 0) {
          this.allPagesFetched = true;
          this.debug('No more products - all pages fetched');
          break;
        }

        this.debug(`Page ${page}: ${products.length} products, caching ALL variants...`);

        // Cache ALL variants from this page FIRST
        for (const product of products) {
          for (const variant of product.variants || []) {
            if (variant.sku) {
              const formattedVariant = this.formatVariantData(variant, product);
              formattedVariant._cachedAt = Date.now();
              formattedVariant._erpCode = variant.sku;
              this.cache.set(variant.sku, formattedVariant);
              totalCachedThisRun++;
              
              // Check if this is what we're looking for (but don't return yet!)
              if (!foundVariant && this.matchesErpCode(variant, erpCode)) {
                foundVariant = formattedVariant;
                this.debug(`✅ Found target SKU at position ${totalCachedThisRun} on page ${page}`);
              }
            }
          }
        }

        this.debug(`Page ${page} complete: cached ${products.length} products. Total cache: ${this.cache.size}`);

        // Mark page as fetched
        this.fetchedPages.add(page - 1);
        
        // If less than 250 products, we've reached the end
        if (products.length < 250) {
          this.allPagesFetched = true;
          this.debug('Last page reached (< 250 products)');
          break;
        }
        
        // If we found what we need AND have cached full page, we can return
        if (foundVariant) {
          this.debug(`Target found! Cached ${totalCachedThisRun} variants from ${page} page(s)`);
          this.saveCacheToStorage();
          return foundVariant;
        }

        page++;
      }

      // Save cache even if not found
      this.saveCacheToStorage();
      
      if (foundVariant) {
        this.debug(`Target found after caching ${totalCachedThisRun} variants`);
        return foundVariant;
      }
      
      this.debug(`Not found after ${totalCachedThisRun} new variants. Total cache: ${this.cache.size}`);
      return null;
      
    } catch (error) {
      console.error('[ERP Mapper] Search error:', error);
      return null;
    }
  }

  /**
   * Enhanced matching logic for ERP codes
   */
  matchesErpCode(variant, erpCode) {
    const normalize = (str) => {
      if (!str) return '';
      return String(str).trim().toUpperCase();
    };

    const normalizedErp = normalize(erpCode);
    const normalizedSku = normalize(variant.sku);
    const normalizedBarcode = normalize(variant.barcode);

    // Direct matches
    if (normalizedSku === normalizedErp) {
      this.debug(`SKU exact match: "${variant.sku}" === "${erpCode}"`);
      return true;
    }

    if (normalizedBarcode === normalizedErp) {
      this.debug(`Barcode exact match: "${variant.barcode}" === "${erpCode}"`);
      return true;
    }

    // Match without hyphens/dashes
    const erpNoHyphens = normalizedErp.replace(/[-_\s]/g, '');
    const skuNoHyphens = normalizedSku.replace(/[-_\s]/g, '');
    const barcodeNoHyphens = normalizedBarcode.replace(/[-_\s]/g, '');

    if (skuNoHyphens === erpNoHyphens && erpNoHyphens !== '') {
      this.debug(`SKU match (no hyphens): "${variant.sku}" ~= "${erpCode}"`);
      return true;
    }

    if (barcodeNoHyphens === erpNoHyphens && erpNoHyphens !== '') {
      this.debug(`Barcode match (no hyphens): "${variant.barcode}" ~= "${erpCode}"`);
      return true;
    }

    return false;
  }

  /**
   * Format variant data for consistent output
   */
  formatVariantData(variant, product) {
    return {
      id: variant.id,
      product_id: product.id,
      title: variant.title,
      product_title: product.title,
      product_handle: product.handle,
      available: variant.available,
      price: variant.price,
      compare_at_price: variant.compare_at_price,
      sku: variant.sku,
      barcode: variant.barcode,
      inventory_quantity: variant.inventory_quantity,
      options: variant.options,
      featured_image: variant.featured_image,
    };
  }

  /**
   * Cache variant data
   */
  cacheVariant(erpCode, variant) {
    variant._cachedAt = Date.now();
    variant._erpCode = erpCode;
    this.cache.set(erpCode, variant);
    this.saveCacheToStorage();
    this.debug(`Cached variant for ${erpCode}`);
  }

  /**
   * Batch lookup multiple ERP codes
   */
  async batchLookup(erpCodes, options = {}) {
    const results = {};
    
    this.debug(`Batch lookup for ${erpCodes.length} codes`);
    
    for (const erpCode of erpCodes) {
      results[erpCode] = await this.getVariantByErpCode(erpCode, options);
    }

    const foundCount = Object.values(results).filter(v => v !== null).length;
    this.debug(`Batch complete: Found ${foundCount}/${erpCodes.length}`);

    return results;
  }

  // ========== Cache Management ==========

  loadCacheFromStorage() {
    try {
      const stored = localStorage.getItem('erp_variant_cache');
      if (stored) {
        const data = JSON.parse(stored);
        
        // Check version compatibility
        if (data.version !== this.cacheVersion || data.storeVersion !== this.storeVersion) {
          this.debug('Cache version mismatch, clearing...');
          this.clearCache();
          return;
        }
        
        // Check age
        const age = Date.now() - data.timestamp;
        if (age < this.cacheExpiry) {
          this.cache = new Map(Object.entries(data.mappings));
          
          // Restore page tracking info
          if (data.fetchedPages) {
            this.fetchedPages = new Set(data.fetchedPages);
          }
          if (data.allPagesFetched) {
            this.allPagesFetched = data.allPagesFetched;
          }
          
          this.debug(`📦 Loaded ${this.cache.size} cached mappings from ${this.fetchedPages.size} pages`);
        } else {
          this.debug('Cache expired, clearing...');
          this.clearCache();
        }
      }
    } catch (error) {
      console.error('[ERP Mapper] Error loading cache:', error);
      this.clearCache();
    }
  }

  saveCacheToStorage() {
    try {
      const data = {
        version: this.cacheVersion,
        storeVersion: this.storeVersion,
        timestamp: Date.now(),
        mappings: Object.fromEntries(this.cache),
        itemCount: this.cache.size,
        
        // Save page tracking info
        fetchedPages: Array.from(this.fetchedPages),
        allPagesFetched: this.allPagesFetched,
      };
      localStorage.setItem('erp_variant_cache', JSON.stringify(data));
      this.debug(`Cache saved: ${this.cache.size} items from ${this.fetchedPages.size} pages`);
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        this.debug('Storage quota exceeded, clearing cache');
        this.clearCache();
      } else {
        console.error('[ERP Mapper] Error saving cache:', error);
      }
    }
  }

  setupInvalidationListeners() {
    document.addEventListener('erp-cache:invalidate', (event) => {
      const { erpCode, reason } = event.detail || {};
      
      if (erpCode) {
        this.debug(`Invalidating cache for ${erpCode}${reason ? `: ${reason}` : ''}`);
        this.cache.delete(erpCode);
        this.saveCacheToStorage();
      } else {
        this.debug(`Clearing all cache${reason ? `: ${reason}` : ''}`);
        this.clearCache();
      }
    });
  }

  clearCache() {
    this.cache.clear();
    this.fetchedPages.clear();
    this.allPagesFetched = false;
    localStorage.removeItem('erp_variant_cache');
    this.debug('🗑️ Cache cleared');
  }

  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      version: this.cacheVersion,
      storeVersion: this.storeVersion,
      fetchedPages: this.fetchedPages.size,
      allPagesFetched: this.allPagesFetched,
    };
  }

  /**
   * Manual cache invalidation for specific ERP code
   */
  invalidateCache(erpCode) {
    if (erpCode) {
      this.cache.delete(erpCode);
      this.saveCacheToStorage();
      this.debug(`Invalidated cache for ${erpCode}`);
    } else {
      this.clearCache();
    }
  }

  /**
   * Pre-fetch all product pages in the background.
   * Skips pages already cached. Once done, all SKU lookups are instant.
   * Safe to run alongside searchAllProducts — both use fetchedPages to avoid duplicates.
   */
  async prefetchAll() {
    if (this.allPagesFetched) {
      this.debug('All pages already fetched, skipping prefetch');
      return;
    }

    if (this._prefetching) {
      this.debug('Prefetch already in progress');
      return;
    }

    this._prefetching = true;
    const startPage = this.fetchedPages.size + 1;
    this.debug(`Background prefetch starting from page ${startPage}, cache has ${this.cache.size} SKUs`);

    try {
      let page = startPage;
      while (page <= this.config.maxSearchPages) {
        // Skip pages already fetched by searchAllProducts running concurrently
        if (this.fetchedPages.has(page - 1)) {
          this.debug(`Prefetch: page ${page} already fetched, skipping`);
          page++;
          continue;
        }

        // Re-check in case searchAllProducts finished everything
        if (this.allPagesFetched) {
          this.debug('Prefetch: allPagesFetched set by another caller, stopping');
          break;
        }

        const url = `/products.json?limit=250&page=${page}`;
        const response = await fetch(url);

        if (!response.ok) {
          this.debug(`Prefetch: API returned ${response.status} on page ${page}`);
          break;
        }

        const data = await response.json();
        const products = data.products || [];

        if (products.length === 0) {
          this.allPagesFetched = true;
          this.debug('Prefetch complete: no more products');
          break;
        }

        for (const product of products) {
          for (const variant of product.variants || []) {
            if (variant.sku) {
              const formatted = this.formatVariantData(variant, product);
              formatted._cachedAt = Date.now();
              formatted._erpCode = variant.sku;
              this.cache.set(variant.sku, formatted);
            }
          }
        }

        this.fetchedPages.add(page - 1);

        if (products.length < 250) {
          this.allPagesFetched = true;
          this.debug(`Prefetch complete at page ${page} (last page)`);
          break;
        }

        page++;
      }

      this.saveCacheToStorage();
      this.debug(`Prefetch done: ${this.cache.size} SKUs cached from ${this.fetchedPages.size} pages`);
    } catch (error) {
      console.warn('[ERP Mapper] Prefetch error:', error);
    } finally {
      this._prefetching = false;
    }
  }
}

// Create global instance
console.log('🚀 ERP Mapper (Client-Side Only) loaded');
window.erpVariantMapper = new ERPVariantMapperClientOnly({
  debug: false, // Set to true for debugging
  maxSearchPages: 40,
});

// Background prefetch after page settles
if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(() => window.erpVariantMapper.prefetchAll());
} else {
  setTimeout(() => window.erpVariantMapper.prefetchAll(), 3000);
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ERPVariantMapperClientOnly;
}